import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { api } from "../lib/api";

/** A worked 22-matchday Governors' League season, with dates on it. */
export const LeagueCalendar = () => {
  const [d, setD] = useState(null);
  const [county, setCounty] = useState("");

  useEffect(() => {
    api.get("/governors-league/calendar", { params: county ? { county } : {} })
      .then((r) => setD(r.data))
      .catch(() => setD(null));
  }, [county]);

  if (!d?.fixtures?.length) return null;

  return (
    <section className="mt-16" data-testid="league-calendar">
      <p className="mono inline-flex items-center gap-2 text-[0.58rem] uppercase tracking-[0.16em] text-ochre">
        <CalendarDays size={12} /> {d.season}
      </p>
      <h2 className="display-3 mt-4">{d.heading}</h2>
      <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.intro}</p>

      <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="calendar-summary">
        {[["Matchdays", d.matchdays, `Across ${d.weeks} weeks with ${d.breaks} clear`],
          ["First kick-off", d.first_kick_off, `Last on ${d.last_kick_off}`],
          ["Counties hosting", d.host_count, county ? `${d.county} only` : "The counties that could host now"],
          ["The season", `$${(d.usd_season || 0).toLocaleString()}`, `$${d.usd_per_fixture} a fixture`]]
          .map(([k, v, note], i) => (
          <div key={k} className="bg-[#0b0b0b] p-6" data-testid={`calendar-summary-${i}`}>
            <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
            <p className="mt-3 font-display text-lg font-bold leading-tight text-white md:text-xl">{v}</p>
            <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="calendar-county-filter">
        <button type="button" onClick={() => setCounty("")}
          data-testid="calendar-county-all"
          className={`border px-4 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
            county ? "border-white/12 text-zinc-500 hover:text-white" : "border-ochre bg-ochre/10 text-ochre"
          }`}>
          Every host county
        </button>
        {(d.hosts || []).map((h) => (
          <button key={h} type="button" onClick={() => setCounty(h)}
            data-testid={`calendar-county-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`border px-4 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
              county === h ? "border-ochre bg-ochre/10 text-ochre" : "border-white/12 text-zinc-500 hover:text-white"
            }`}>
            {h}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto border border-zinc-900">
        <table className="w-full min-w-[880px] text-left" data-testid="calendar-table">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["MD", "Date and kick-off", "Round", "County", "Ground", "Running cost"].map((h) => (
                <th key={h} className="mono px-4 py-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.fixtures.map((f) => (
              <tr key={f.matchday} className="border-b border-zinc-900 align-top"
                data-testid={`calendar-fixture-${f.matchday}`}>
                <td className="mono px-4 py-3.5 text-sm text-ochre">{f.matchday}</td>
                <td className="px-4 py-3.5 text-xs text-white">
                  {f.day}
                  <span className="mono mt-1 block text-[0.55rem] tracking-[0.14em] text-zinc-500">{f.kick_off}</span>
                </td>
                <td className="px-4 py-3.5 text-xs leading-relaxed text-zinc-400">{f.round}</td>
                <td className="px-4 py-3.5 text-xs text-zinc-300">{f.county}</td>
                <td className="px-4 py-3.5 text-xs leading-relaxed text-zinc-400">
                  {f.venue}
                  <span className="mono mt-1 block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-600">
                    {f.venue_type}
                  </span>
                </td>
                <td className="mono px-4 py-3.5 text-xs text-zinc-400">
                  ${f.running_total_usd.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-5 max-w-3xl text-xs leading-relaxed text-zinc-500">{d.note}</p>
    </section>
  );
};
